import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Package, ArrowLeft, User, Mail, Lock, Fingerprint } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useLanguage } from '@/hooks/useLanguage';
import { useBiometric } from '@/hooks/useBiometric';
import { useToast } from '@/hooks/use-toast';

const Auth = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [signInData, setSignInData] = useState({ email: '', password: '' });
  const [signUpData, setSignUpData] = useState({ email: '', password: '', fullName: '' });
  const [enableBiometricPrompt, setEnableBiometricPrompt] = useState(false);
  
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const { t } = useLanguage();
  const { toast } = useToast();
  const { 
    isAvailable: isBiometricAvailable, 
    biometryType, 
    isChecking,
    authenticate, 
    getCredentials, 
    setCredentials,
    deleteCredentials
  } = useBiometric();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Check for saved biometric credentials on mount
  useEffect(() => {
    if (isBiometricAvailable && !isChecking) {
      checkForBiometricLogin();
    }
  }, [isBiometricAvailable, isChecking]);

  const checkForBiometricLogin = async () => {
    try {
      const credentials = await getCredentials();
      if (credentials.username && credentials.password) {
        // User has biometric credentials saved, show biometric button
      }
    } catch (error) {
      console.log('No biometric credentials saved');
    }
  };

  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    try {
      const authResult = await authenticate(t('auth.biometric_authenticate'));
      
      if (authResult.success) {
        const credentials = await getCredentials();
        
        if (credentials.username && credentials.password) {
          const { error } = await signIn(credentials.username, credentials.password);
          
          if (!error) {
            navigate('/dashboard');
          }
        }
      } else {
        toast({
          title: t('auth.biometric_error'),
          description: authResult.error || 'Authentication failed',
          variant: "destructive"
        });
      }
    } catch (error: any) {
      toast({
        title: t('auth.biometric_error'),
        description: error.message || 'Biometric authentication failed',
        variant: "destructive"
      });
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    const { error } = await signIn(signInData.email, signInData.password);
    
    if (!error) {
      // After successful login, prompt to enable biometric if available
      if (isBiometricAvailable) {
        setEnableBiometricPrompt(true);
      } else {
        navigate('/dashboard');
      }
    }
    
    setLoading(false);
  };

  const handleEnableBiometric = async () => {
    try {
      await setCredentials(signInData.email, signInData.password);
      toast({
        title: t('auth.biometric_enabled'),
        description: 'You can now use biometric authentication to sign in',
      });
      navigate('/dashboard');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to enable biometric authentication',
        variant: "destructive"
      });
      navigate('/dashboard');
    }
  };

  const handleSkipBiometric = () => {
    navigate('/dashboard');
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    const { error } = await signUp(signUpData.email, signUpData.password, signUpData.fullName);
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 mb-6 text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span>{t('auth.back_to_home')}</span>
          </Link>
          
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-primary">
              <Package className="h-7 w-7 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              {settings.site_name}
            </h1>
          </div>
          
          <p className="text-muted-foreground">
            {settings.site_tagline}
          </p>
        </div>

        {/* Auth Tabs */}
        <Card className="shadow-xl border-0 bg-gradient-card">
          <CardHeader className="text-center">
            <CardTitle>{t('auth.access_account')}</CardTitle>
            <CardDescription>
              {t('auth.connect_or_create')}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="signin">{t('auth.signin')}</TabsTrigger>
                <TabsTrigger value="signup">{t('auth.signup')}</TabsTrigger>
              </TabsList>
              
              {/* Sign In Tab */}
              <TabsContent value="signin">
                {enableBiometricPrompt ? (
                  <div className="space-y-4 text-center py-6">
                    <div className="flex justify-center">
                      <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center">
                        <Fingerprint className="h-8 w-8 text-primary-foreground" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-lg">{t('auth.biometric_setup')}</h3>
                      <p className="text-sm text-muted-foreground">
                        {t('auth.use_biometric').replace('{type}', 
                          biometryType === 'faceId' ? t('auth.biometric_faceid') :
                          biometryType === 'touchId' ? t('auth.biometric_touchid') :
                          t('auth.biometric_fingerprint')
                        )}
                      </p>
                    </div>
                    <div className="space-y-3">
                      <Button 
                        onClick={handleEnableBiometric}
                        className="w-full bg-gradient-primary hover:shadow-glow"
                      >
                        {t('auth.biometric_setup')}
                      </Button>
                      <Button 
                        onClick={handleSkipBiometric}
                        variant="ghost"
                        className="w-full"
                      >
                        Skip
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <form onSubmit={handleSignIn} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">{t('auth.email')}</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="email"
                            type="email"
                            placeholder={t('auth.email_placeholder')}
                            value={signInData.email}
                            onChange={(e) => setSignInData(prev => ({ ...prev, email: e.target.value }))}
                            className="pl-10"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password">{t('auth.password')}</Label>
                          <Link 
                            to="/forgot-password" 
                            className="text-xs text-primary hover:underline"
                          >
                            {t('auth.forgot_password')}
                          </Link>
                        </div>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder={t('auth.password_placeholder')}
                            value={signInData.password}
                            onChange={(e) => setSignInData(prev => ({ ...prev, password: e.target.value }))}
                            className="pl-10 pr-10"
                            required
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2 p-0"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="w-full bg-gradient-primary hover:shadow-glow" 
                        disabled={loading}
                      >
                        {loading ? t('auth.signin_loading') : t('auth.signin_button')}
                      </Button>
                    </form>

                    {isBiometricAvailable && !isChecking && (
                      <>
                        <div className="relative my-4">
                          <Separator />
                          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                            {t('common.or')}
                          </span>
                        </div>
                        
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          onClick={handleBiometricLogin}
                          disabled={biometricLoading}
                        >
                          <Fingerprint className="h-4 w-4 mr-2" />
                          {biometricLoading 
                            ? t('auth.signin_loading')
                            : t('auth.use_biometric').replace('{type}', 
                                biometryType === 'faceId' ? t('auth.biometric_faceid') :
                                biometryType === 'touchId' ? t('auth.biometric_touchid') :
                                t('auth.biometric_fingerprint')
                              )
                          }
                        </Button>
                      </>
                    )}
                  </>
                )}
              </TabsContent>
              
              {/* Sign Up Tab */}
              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">{t('auth.full_name')}</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="fullName"
                        type="text"
                        placeholder={t('auth.full_name_placeholder')}
                        value={signUpData.fullName}
                        onChange={(e) => setSignUpData(prev => ({ ...prev, fullName: e.target.value }))}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signupEmail">{t('auth.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="signupEmail"
                        type="email"
                        placeholder={t('auth.email_placeholder')}
                        value={signUpData.email}
                        onChange={(e) => setSignUpData(prev => ({ ...prev, email: e.target.value }))}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signupPassword">{t('auth.password')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="signupPassword"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('auth.password_placeholder')}
                        value={signUpData.password}
                        onChange={(e) => setSignUpData(prev => ({ ...prev, password: e.target.value }))}
                        className="pl-10 pr-10"
                        required
                        minLength={6}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-primary hover:shadow-glow" 
                    disabled={loading}
                  >
                    {loading ? t('auth.signup_loading') : t('auth.signup_button')}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        <p className="text-center text-sm text-muted-foreground mt-6">
          {t('auth.terms_accept')}{' '}
          <Link to="/terms" className="text-primary hover:underline">
            {t('auth.terms_link')}
          </Link>{' '}
          {t('auth.and')}{' '}
          <Link to="/privacy" className="text-primary hover:underline">
            {t('auth.privacy_link')}
          </Link>
          .
        </p>
      </div>
    </div>
  );
};

export default Auth;